import { useState } from "react";
import {
  IconButton,
  Tooltip,
  Dialog,
  CircularProgress,
  Box,
} from "@mui/material";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CloseIcon from "@mui/icons-material/Close";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";

interface VideoCellProps {
  link: string;
  customFileName?: string;
}

const VideoCell: React.FC<VideoCellProps> = ({ link, customFileName }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [downloadLoader, setDownloadLoader] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const getMimeType = (url: string) => {
    if (url.endsWith(".webm")) return "video/webm";
    if (url.endsWith(".ogv") || url.endsWith(".ogg")) return "video/ogg";
    if (url.endsWith(".m3u8")) return "application/x-mpegURL";
    if (url.endsWith(".mpd")) return "application/dash+xml";
    if (url.endsWith(".avi")) return "video/x-msvideo";
    if (url.endsWith(".mov")) return "video/quicktime";
    if (url.endsWith(".wmv")) return "video/x-ms-wmv";
    if (url.endsWith(".flv")) return "video/x-flv";
    if (url.endsWith(".3gp")) return "video/3gpp";
    if (url.endsWith(".mkv")) return "video/x-matroska";
    return "video/mp4";
  };

  const handleDownloadFile = (url: string, customFilename?: string) => {
    const link = document.createElement("a");
    link.href = url;

    if (customFilename) {
      link.download = customFilename;
    } else {
      link.download = url.split("/").pop()?.split("?")[0] || "download";
    }

    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Box display={"flex"}>
      <Tooltip title={downloadLoader ? "Downloading..." : "Download File"}>
        <IconButton
          onClick={() => handleDownloadFile(link, customFileName)}
          color="primary"
          size="small"
        >
          {downloadLoader ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <DownloadForOfflineIcon />
          )}
        </IconButton>
      </Tooltip>

      {link && (
        <Tooltip title="Play Video">
          <IconButton onClick={handleOpen} color="primary" size="small">
            <PlayCircleIcon />
          </IconButton>
        </Tooltip>
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          style: { backgroundColor: "transparent", boxShadow: "none" },
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "8px",
          }}
        >
          <IconButton onClick={handleClose}>
            <CloseIcon sx={{ color: "#fff" }} fontSize="large" />
          </IconButton>
        </div>
        <video
          controls
          autoPlay
          style={{
            width: "auto",
            maxWidth: "90%",
            height: "75vh",
            display: "block",
            margin: "auto",
          }}
        >
          <source src={encodeURI(link)} type={getMimeType(link)} />
          Your browser does not support the video tag.
        </video>
      </Dialog>
    </Box>
  );
};

export default VideoCell;
